/**
 * 客服入口兼容层。
 * SaleSmartly 已停用（实现保留在下方注释中，便于恢复）；
 * 对外 API 名不变，内部转发到 Chatwoot。
 */
import type { AppProfile } from '@/api/auth';
import { openChatwoot, resetChatwootUser, sendChatwootTextMessage, syncChatwootUser } from '@/lib/chatwoot';

export const GUEST_WELCOME_MESSAGE = '您好，有什么可以帮助您的';
export const HOME_GUEST_WELCOME_DELAY_MS = 8000;
export const GUEST_WELCOME_EVENT = 'mh-ssq-guest-welcome';

/** 仅当前页面生命周期内防重复；整页刷新后未登录首页会再弹 */
let guestWelcomeShownThisLoad = false;

export function hasShownHomeGuestWelcome(): boolean {
  return guestWelcomeShownThisLoad;
}

export function markHomeGuestWelcomeShown(): void {
  guestWelcomeShownThisLoad = true;
  try {
    sessionStorage.removeItem('mh_ssq_home_guest_welcome');
    sessionStorage.removeItem('mh_ssq_home_guest_welcome_v2');
  } catch {
    /* ignore */
  }
}

export function buildSaleSmartlyDescription(profile: Partial<AppProfile>): string {
  const parts: string[] = [];
  if (profile.memberLevelName) parts.push(`会员：${profile.memberLevelName}`);
  if (profile.memberPermanent === '1') {
    parts.push('永久会员');
  } else if (profile.memberExpireTime) {
    parts.push(`到期：${profile.memberExpireTime}`);
  }
  if (profile.pointsBalance != null) parts.push(`积分：${profile.pointsBalance}`);
  if (profile.walletBalance != null) parts.push(`余额：${profile.walletBalance}`);
  return parts.join('\n') || 'C端用户';
}

/** 登录后同步客服身份（需 profile 带 chatwootIdentifierHash） */
export function syncSaleSmartlyLogin(
  profile: Partial<AppProfile> & { userId?: number; userName?: string },
): void {
  const userId = profile.userId;
  const userName = profile.userName;
  const hash = profile.chatwootIdentifierHash;
  if (userId == null || !userName || !hash) {
    return;
  }
  syncChatwootUser(String(userId), userName, hash);
}

/** 登录接口暂无 hash，身份同步交由 ChatwootBootstrap + profile */
export function syncSaleSmartlyLoginFromAuthUser(_user: {
  userId: number;
  userName: string;
  email?: string;
  memberLevelName?: string;
}): void {
  // Chatwoot：登录后由 ChatwootBootstrap 拉 profile 再 setUser
  void _user;
}

/** 游客身份：Chatwoot 气泡即可，无需推游客登录信息 */
export function syncSaleSmartlyGuest(): void {
  // no-op under Chatwoot
}

/** 退出登录 / 401：重置 Chatwoot 身份 */
export function clearSaleSmartlyUser(): void {
  resetChatwootUser();
}

/** 未登录首页欢迎弹窗（Chatwoot 期已停用自动弹窗，保留 API） */
export function openSaleSmartlyGuestWelcome(): boolean {
  return false;
}

/**
 * 打开客服（Chatwoot / 兼容桥接）。
 * 有 message 时先代发访客文字消息（聊天窗会自动展开），再确保打开。
 * @returns 是否已触发打开客服
 */
export function openSaleSmartlyChat(options?: { message?: string }): boolean {
  const message = options?.message?.trim();
  if (message) {
    sendChatwootTextMessage(message);
  }
  openChatwoot();
  return true;
}

/* ============================================================================
 * ========== SaleSmartly 原实现（暂时停用，保留以便恢复）==========
 *
 * import type { AppProfile } from '@/api/auth';
 * import { getToken } from '@/lib/auth-storage';
 *
 * const GUEST_ID_KEY = 'mh_ssq_guest_id';
 * const IDENTITY_CACHE_KEY = 'mh_ssq_identity';
 * ... pushSsq / setLoginInfo / chatOpen / clearUser ...
 *
 * 恢复步骤：
 * 1. 取消 index.html 中 SaleSmartly 脚本注释，并注释 Chatwoot 脚本
 * 2. App.tsx 恢复 SaleSmartlyBootstrap / SaleSmartlyFloatingButton
 * 3. 将本文件恢复为 git 历史中的 SaleSmartly 完整实现
 * ==================================================================
 */
